import { extendTheme } from "@mui/joy/styles";

declare module "@mui/joy/styles" {
  interface TypographySystemOverrides {
    modalHeading: true;
    mediumHeading: true;
    dashboardHeading: true;
    pageTitle: true;
    sectionHeading: true;
    description: true;
    label: true;
  }

  interface PaletteRangeOverrides {
    tertiary: true;
    accent: true;
  }

  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true; // ✅ Add xxl here
  }
}

const baseTheme = extendTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 2200,
    },
  },
  fontFamily: {
    body: "Inter, sans-serif",
  },
  fontWeight: {
    sm: 300,
    md: 400,
    lg: 400,
    xl: 500,
  },
  colorSchemes: {
    dark: {
      palette: {
        background: {
          body: '#f4f4f4',
        },
      },
    },
  },
  components: {
    JoyTypography: {
      styleOverrides: {
        root: {
          fontFamily: "Inter, sans-serif",
        },
      },
    },
    JoyButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          fontSize: "14px",
          [theme.breakpoints.up("sm")]: {
            fontSize: "16px",
          },
          borderRadius: "12px",
          padding: "12px 24px",
          fontFamily: "Inter, sans-serif",
          lineHeight: 1.5,
          textTransform: "none",
          border: "1px solid transparent",
          fontWeight: "400",
          ...(ownerState.variant === "solid" && {
            backgroundColor: "#2d2e84",
            color: "#fff",
            borderRadius: "16px",
            fontWeight: 600,
            height: "52px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#232478",
            },
            "&.Mui-disabled": {
              backgroundColor: "#2e2d7e",
              opacity: 0.7,
            },
          }),
          ...(ownerState.variant === "outlined" && {
            height: "52px",
            color: "#2e2d7e",
            backgroundColor: "white",
            "&:hover": {
              backgroundColor: "white",
            },
          }),
        }),
      },
    },
    JoySnackbar: {
      styleOverrides: {
        root: {
          backgroundColor: "white",
          color: "#1c1c1c",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
          padding: "12px 16px",
          minWidth: "320px",
          maxWidth: "100%",
          borderLeft: "4px solid #4CAF50",
          fontWeight: 400,
          fontSize: "14px",
          fontFamily: "Inter, sans-serif",
          "& .MuiSnackbar-content": {
            padding: 0,
            fontFamily: "Inter, sans-serif",
          },
          "& .MuiSnackbar-startDecorator": {
            marginRight: "8px",
          },
          "& .MuiSnackbar-endDecorator": {
            marginLeft: "8px",
          },
        },
      },
    },
    JoyLink: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          fontWeight: 600,
          color: "#2E2D7E",
          textDecoration: "underline",
          fontFamily: "Inter, sans-serif",
        },
      },
    },
    JoyCard: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          boxShadow: "none",
          border: "none",
          backgroundColor: "#FFFFFF",
        },
      },
    },
    JoyAvatar: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          padding: "8px",
          bgcolor: "#f5f5f5",
        },
      },
    },
    JoyChip: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          fontWeight: "500",
          backgroundColor: "#E9E8F7",
          color: "#2E2D7E",
          borderRadius: "8px",
          padding: "4px 8px",
        },
      },
    },
    JoySlider: {
      styleOverrides: {
        root: {
          padding: "20px 0",
        },
        track: {
          backgroundColor: "#2E2D7E",
          height: "6px",
          borderRadius: "4px",
        },
        rail: {
          backgroundColor: "#E9E8F7",
          height: "6px",
          borderRadius: "4px",
        },
        thumb: {
          width: "20px",
          height: "20px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 6px 13px 0px #0000001F, 0px 0.5px 4px 0px #0000001F",
          "::before": {
            border: "none",
            outline: "none",
          },
        },
      },
    },
  },
  typography: {
    modalHeading: {
      fontFamily: "Inter",
      fontWeight: "600",
      color: "#1A1B1D",
      fontSize: "24px",
    },
    dashboardHeading: {
      fontFamily: "Inter",
      fontWeight: "600",
      color: "#1A1B1D",
      fontSize: "16px",
    },
    mediumHeading: {
      fontFamily: "Inter",
      fontWeight: "500",
      color: "#1A1B1D",
      fontSize: "16px",
    },
    description: {
      color: "#7b7f85",
      fontSize: "1rem",
      fontFamily: "Inter",
      fontWeight: "400",
    },
    label: {
      fontSize: "16px",
      color: "#1A1B1D",
      fontWeight: "500",
    },
  },
});

export { baseTheme };
